import { DeviceModels, DeviceTypes } from '@eppendorf/app-configs';
import {
  GetDeviceSnapshotDTO,
  TelemetryHistoryEnrichedDTO,
} from '@eppendorf/vnls-device-service-utils';
import i18next from 'i18next';

import { useDeviceTypeConfig } from '$components/app-config/useDeviceTypeConfig';
// import { createOfflineTelemetryPlaceholder } from '$features/monitoring/monitoring.utils.snapshot';
import { findRealtimeForParamFromSnapshot } from '$features/monitoring/shared/find-realtime-for-param-snapshot';

function assignTelemDataToPrimaryAndSecondary(
  acc: {
    primaryParameters: TelemetryHistoryEnrichedDTO[];
    secondaryParameters: TelemetryHistoryEnrichedDTO[];
  },
  item: TelemetryHistoryEnrichedDTO,
) {
  const SECONDARY_PARAMETERS = new Set(['WaterLevel', 'CO2Tank']);
  if (SECONDARY_PARAMETERS.has(item.telemetryId)) {
    if (item.telemetryId === 'WaterLevel') {
      acc.secondaryParameters.push({
        ...item,
        valueString: i18next.t(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- translation key, want to see unexpected values
          `monitoringDetail.overwrite.waterLevel.${item?.valueString ?? 'default'}` as any,
        ),
      });
    } else if (item.telemetryId === 'CO2Tank') {
      acc.secondaryParameters.push({
        ...item,
        valueString: i18next.t(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- translation key, want to see unexpected values
          `monitoringDetail.overwrite.co2Tank.${item?.valueString ?? 'default'}` as any,
        ),
      });
    } else {
      acc.secondaryParameters.push(item);
    }
  } else {
    acc.primaryParameters.push(item);
  }

  return acc;
}

export function useDeviceTelemetryDataForSnapshot(snapshot?: GetDeviceSnapshotDTO): {
  primaryParameters: TelemetryHistoryEnrichedDTO[];
  secondaryParameters: TelemetryHistoryEnrichedDTO[];
} {
  const deviceTypeConfig = useDeviceTypeConfig(snapshot?.inventoryDevice);

  if (!snapshot) {
    return { primaryParameters: [], secondaryParameters: [] };
  }

  //
  // for known devices, that we have a config for, we only show the parameters that are in the config
  //
  if (
    deviceTypeConfig?.parameters?.length &&
    deviceTypeConfig?.type !== DeviceTypes.GENERIC_DEVICE
  ) {
    const invDevice = snapshot?.inventoryDevice;
    const deviceParameters = [
      ...deviceTypeConfig.parameters,
      ...(deviceTypeConfig.custom?.[invDevice?.model as DeviceModels]?.parameters ?? []),
      ...(deviceTypeConfig.custom?.[invDevice?.model as DeviceModels]
        ?.additionalParameters ?? []),
    ];

    const telemetryDataToShow = deviceParameters.map((param) => {
      const telem = findRealtimeForParamFromSnapshot(
        snapshot?.latestTelemetry ?? [],
        param,
      );

      if (!telem) {
        return null;
        // we could return a placeholder for offline telemetry here to show parameters even if they dont have telemetry data (yet)
        // return createOfflineTelemetryPlaceholder(param.subject, param.name);
      }

      if (param.name) {
        return {
          ...telem,
          name: param.name,
        };
      }

      return telem;
    });

    return telemetryDataToShow
      .filter((data): data is TelemetryHistoryEnrichedDTO => Boolean(data))
      .reduce(assignTelemDataToPrimaryAndSecondary, {
        primaryParameters: [],
        secondaryParameters: [],
      });
  }

  const allTelemetryData = [
    ...(snapshot.latestTelemetry.reduce((map, telem) => {
      if (!map.has(telem?.telemetryId)) {
        map.set(telem?.telemetryId, telem);
      }
      return map;
    }, new Map<string, TelemetryHistoryEnrichedDTO>()) || []),
  ]
    .map(([_, realtimeData]) => realtimeData)
    .reduce(assignTelemDataToPrimaryAndSecondary, {
      primaryParameters: [],
      secondaryParameters: [],
    });

  //
  // for known devices, that we don't have a config for, we show all telemetry data
  //
  if (deviceTypeConfig?.type !== DeviceTypes.GENERIC_DEVICE) {
    return allTelemetryData;
  }

  //
  // for generic devices, we show all telemetry data but some of them are sorted according to the config
  // and others may be filtered out by the blocklist from the config
  //
  allTelemetryData.primaryParameters = allTelemetryData.primaryParameters.filter(
    (param) => !deviceTypeConfig?.parametersBlocklist?.includes(param.telemetryId),
  );

  // Apply configured display names to generic telemetry and order known parameters
  // according to the config, while keeping unconfigured telemetry visible afterward.
  const modelCustom =
    deviceTypeConfig.custom?.[snapshot.inventoryDevice.model as DeviceModels];
  const configuredParameters = [
    ...(deviceTypeConfig.parameters ?? []),
    ...(modelCustom?.parameters ?? []),
    ...(modelCustom?.additionalParameters ?? []),
  ];
  const configuredParameterBySubject = new Map(
    configuredParameters.map((parameter) => [parameter.subject, parameter]),
  );
  const configuredIndexBySubject = new Map(
    configuredParameters.map((parameter, index) => [parameter.subject, index]),
  );

  allTelemetryData.primaryParameters = allTelemetryData.primaryParameters.map(
    (telemetry) => {
      const parameterConfig = configuredParameterBySubject.get(telemetry.telemetryId);
      return parameterConfig?.name
        ? { ...telemetry, name: parameterConfig.name }
        : telemetry;
    },
  );

  allTelemetryData.primaryParameters.sort((a, b) => {
    const aIndex = configuredIndexBySubject.get(a.telemetryId);
    const bIndex = configuredIndexBySubject.get(b.telemetryId);

    if (aIndex === undefined && bIndex === undefined) return 0;
    if (aIndex === undefined) return 1;
    if (bIndex === undefined) return -1;
    return aIndex - bIndex;
  });

  return allTelemetryData;
}
